import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axiosInstance from 'utils/axios-instance';
import './index.css';

interface Community {
    uuid: string;
    type: string;
    name: string;
    description: string;
    geoFeaturesName: string;
    userCount: number;
    createdAt: string;
}

interface Post {
    postId: number;
    title: string;
    content: string;
    createdBy: number;
    createdAt: string;
    likeCount: number;
    viewCount: number;
    replyCount: number;
    community?: {
        uuid: string;
        name: string;
        type: string;
        geoFeaturesName: string;
    };
}

interface CommunityListData {
    buildingCommunities: Community[];
    townCommunities: Community[];
}

interface PostListData {
    buildingPosts: Post[];
    townPosts: Post[];
}

const CommunityPage: React.FC = () => {
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState<'all' | 'popular' | 'recent'>('popular');
    const [communities, setCommunities] = useState<CommunityListData>({
        buildingCommunities: [],
        townCommunities: []
    });
    const [recentPosts, setRecentPosts] = useState<PostListData>({
        buildingPosts: [],
        townPosts: []
    });
    const [loading, setLoading] = useState(true);
    const [communityLoading, setCommunityLoading] = useState(false);

    useEffect(() => {
        fetchInitialData();
    }, []);

    useEffect(() => {
        if (!loading) {
            fetchCommunities();
        }
    }, [activeTab]);

    const fetchInitialData = async () => {
        try {
            setLoading(true);
            
            // 최근 게시글 조회
            const postsResponse = await axiosInstance.get('/api/community/posts/recent?limit=12');
            setRecentPosts(postsResponse.data);

            // 초기 커뮤니티 목록 조회 (인기)
            const communityResponse = await axiosInstance.get('/api/community/popular?limit=20');
            setCommunities(communityResponse.data);
        } catch (error) {
            console.error('데이터 조회 실패:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchCommunities = async () => {
        try {
            setCommunityLoading(true);
            
            // 커뮤니티 목록 조회
            let endpoint;
            switch (activeTab) {
                case 'popular':
                    endpoint = '/api/community/popular?limit=20';
                    break;
                case 'recent':
                    endpoint = '/api/community/recent?limit=20';
                    break;
                default:
                    endpoint = '/api/community/list?limit=50';
            }
            
            const communityResponse = await axiosInstance.get(endpoint);
            setCommunities(communityResponse.data);
        } catch (error) {
            console.error('커뮤니티 조회 실패:', error);
        } finally {
            setCommunityLoading(false);
        }
    };

    const handleCommunityClick = (community: Community) => {
        if (community.type === 'building') {
            navigate(`/community/building/${community.uuid}`);
        } else {
            navigate(`/community/town?uuid=${community.uuid}`);
        }
    };

    const handlePostClick = (post: Post) => {
        if (post.community) {
            if (post.community.type === 'building') {
                navigate(`/community/building/${post.community.uuid}/post/${post.postId}`);
            } else {
                navigate(`/community/town/${post.community.uuid}/post/${post.postId}`);
            }
        }
    };

    // HTML 태그 제거 및 텍스트 추출 함수
    const stripHtml = (html: string): string => {
        const tmp = document.createElement('div');
        tmp.innerHTML = html;
        return tmp.textContent || tmp.innerText || '';
    };

    // 상대 시간 표시 함수
    const getRelativeTime = (dateString: string): string => {
        const now = new Date();
        const past = new Date(dateString);
        const diffInSeconds = Math.floor((now.getTime() - past.getTime()) / 1000);

        if (diffInSeconds < 60) {
            return '방금 전';
        } else if (diffInSeconds < 3600) {
            const minutes = Math.floor(diffInSeconds / 60);
            return `${minutes}분 전`;
        } else if (diffInSeconds < 86400) {
            const hours = Math.floor(diffInSeconds / 3600);
            return `${hours}시간 전`;
        } else if (diffInSeconds < 604800) {
            const days = Math.floor(diffInSeconds / 86400);
            return `${days}일 전`;
        } else if (diffInSeconds < 2592000) {
            const weeks = Math.floor(diffInSeconds / 604800);
            return `${weeks}주 전`;
        } else if (diffInSeconds < 31536000) {
            const months = Math.floor(diffInSeconds / 2592000);
            return `${months}개월 전`;
        } else {
            const years = Math.floor(diffInSeconds / 31536000);
            return `${years}년 전`;
        }
    };

    if (loading) {
        return (
            <div className="community-page">
                <div className="loading-container">
                    <div className="loader"></div>
                    <p>로딩 중...</p>
                </div>
            </div>
        );
    }

    const allPosts = [...recentPosts.buildingPosts, ...recentPosts.townPosts]
        .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
        .slice(0, 12);

    return (
        <div className="community-page">
            <div className="community-container">
                {/* 헤더 */}
                <div className="page-header">
                    <h1>커뮤니티</h1>
                    <p>우리 동네, 우리 건물의 이야기를 나눠보세요</p>
                </div>

                {/* 최근 게시글 섹션 */}
                <section className="recent-posts-section">
                    <div className="section-header">
                        <h2>최근 게시글</h2>
                        <p>커뮤니티에서 활발하게 소통되는 이야기들</p>
                    </div>
                    
                    {allPosts.length === 0 ? (
                        <div className="empty-state">
                            <p>아직 작성된 게시글이 없습니다</p>
                        </div>
                    ) : (
                        <div className="posts-grid">
                            {allPosts.map((post) => (
                                <div
                                    key={post.postId}
                                    className="post-card"
                                    onClick={() => handlePostClick(post)}
                                >
                                    <div className="post-header">
                                        <span className={`community-badge ${post.community?.type}`}>
                                            {post.community?.type === 'building' ? '건물' : '동네'}
                                        </span>
                                        <span className="post-time">{getRelativeTime(post.createdAt)}</span>
                                    </div>
                                    <h3 className="post-title">{post.title}</h3>
                                    <p className="post-content">{stripHtml(post.content)}</p>
                                    <div className="post-footer">
                                        <span className="community-name">{post.community?.name}</span>
                                        <div className="post-stats">
                                            <span className="stat-item">
                                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                                                    <circle cx="12" cy="12" r="3"></circle>
                                                </svg>
                                                {post.viewCount}
                                            </span>
                                            <span className="stat-item">
                                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
                                                </svg>
                                                {post.replyCount}
                                            </span>
                                            <span className="stat-item">
                                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                    <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>
                                                </svg>
                                                {post.likeCount}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </section>

                {/* 커뮤니티 목록 섹션 */}
                <section className="communities-section">
                    <div className="section-header">
                        <h2>커뮤니티 둘러보기</h2>
                        
                        {/* 탭 네비게이션 */}
                        <div className="community-tabs">
                            <button
                                className={`community-tab-button ${activeTab === 'popular' ? 'active' : ''}`}
                                onClick={() => setActiveTab('popular')}
                            >
                                인기
                            </button>
                            <button
                                className={`community-tab-button ${activeTab === 'recent' ? 'active' : ''}`}
                                onClick={() => setActiveTab('recent')}
                            >
                                최신
                            </button>
                            <button
                                className={`community-tab-button ${activeTab === 'all' ? 'active' : ''}`}
                                onClick={() => setActiveTab('all')}
                            >
                                전체
                            </button>
                        </div>
                    </div>

                    {communityLoading ? (
                        <div className="community-loading">
                            <div className="loader"></div>
                            <p>커뮤니티 목록을 불러오는 중...</p>
                        </div>
                    ) : (
                        <>
                            {/* 건물 커뮤니티 */}
                            {communities.buildingCommunities.length > 0 && (
                                <div className="community-group">
                                    <h3 className="group-title">건물 커뮤니티</h3>
                                    <div className="community-list">
                                        {communities.buildingCommunities.map((community) => (
                                            <div
                                                key={community.uuid}
                                                className="community-item"
                                                onClick={() => handleCommunityClick(community)}
                                            >
                                                <div className="community-main">
                                                    <div className="community-name-section">
                                                        <h4>{community.name}</h4>
                                                        <span className="location">{community.geoFeaturesName}</span>
                                                    </div>
                                                    <span className="member-count">{community.userCount.toLocaleString()}명</span>
                                                </div>
                                                {community.description && (
                                                    <p className="community-desc">{community.description}</p>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* 동네 커뮤니티 */}
                            {communities.townCommunities.length > 0 && (
                                <div className="community-group">
                                    <h3 className="group-title">동네 커뮤니티</h3>
                                    <div className="community-list">
                                        {communities.townCommunities.map((community) => (
                                            <div
                                                key={community.uuid}
                                                className="community-item"
                                                onClick={() => handleCommunityClick(community)}
                                            >
                                                <div className="community-main">
                                                    <div className="community-name-section">
                                                        <h4>{community.name}</h4>
                                                        <span className="location">{community.geoFeaturesName}</span>
                                                    </div>
                                                    <span className="member-count">{community.userCount.toLocaleString()}명</span>
                                                </div>
                                                {community.description && (
                                                    <p className="community-desc">{community.description}</p>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {communities.buildingCommunities.length === 0 && communities.townCommunities.length === 0 && (
                                <div className="empty-state">
                                    <p>등록된 커뮤니티가 없습니다</p>
                                </div>
                            )}
                        </>
                    )}
                </section>
            </div>
        </div>
    );
};

export default CommunityPage;
